/**
 * A production's day list, and which day a page should open on.
 *
 * Every producer page that has a Day 1 / Day 2 selector derived this the same
 * way inline (expand start..end, then "today if the festival is running, else
 * day one"). It lives here so the pages and the route prefetcher can't drift —
 * a prefetch that guessed a different day would warm the wrong cache entry and
 * the page would still fetch on arrival.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** yyyy-MM-dd for a UTC timestamp. */
function toIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Parse yyyy-MM-dd (ignoring any time part) to UTC midnight. */
function parseIsoDate(value: string): number | null {
  const ms = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Every festival day from `start` to `end`, inclusive, as yyyy-MM-dd.
 *
 * A missing end date means a single-day production. Returns an empty list when
 * there is no start date or the dates are unusable.
 */
export function eventDatesBetween(start: string | null | undefined, end?: string | null): string[] {
  if (!start) return [];
  const startMs = parseIsoDate(start);
  if (startMs === null) return [];

  const endMs = end ? parseIsoDate(end) : startMs;
  if (endMs === null || endMs < startMs) return [toIsoDate(startMs)];

  const days: string[] = [];
  for (let ms = startMs; ms <= endMs; ms += DAY_MS) {
    days.push(toIsoDate(ms));
  }
  return days;
}

/**
 * Today as yyyy-MM-dd in the viewer's own timezone — the value the day
 * selectors compare against, and what a page prefetcher must use to guess the
 * same day the page will land on.
 */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * A PROduce festival day runs 06:00 → 05:59 the next morning; a shift or
 * lineup slot after midnight belongs to the PREVIOUS calendar day's
 * event_date. One constant, shared with effectiveShift.ts's placement
 * maths and mirrored by the SQL helpers (migration 20260524120000).
 */
export const FESTIVAL_DAY_ROLLOVER_HOUR = 6;

const ROLLOVER_MS = FESTIVAL_DAY_ROLLOVER_HOUR * 60 * 60 * 1000;

/**
 * WHICH FESTIVAL DAY it is right now — not which calendar day.
 *
 * At 00:30 during Untold's Saturday night, the calendar says Sunday but the
 * crew is mid-way through Saturday's event_date: 95 assignments and 80
 * lineup slots of that production sat after midnight, and every day
 * selector that compared against the calendar date flipped to the next
 * day's programme at midnight (the UTC-based ones at 03:00 local). Shift
 * the clock back by the rollover before taking the date and the selectors
 * agree with the event_date convention.
 *
 * `timeZone`: pass the briefing's timezone on producer surfaces — a
 * producer can watch from anywhere. Crew surfaces omit it: a crew device
 * at the festival is in the festival's zone.
 *
 * Known 1-hour drift, accepted: this subtracts six REAL hours, while the
 * event_date convention is wall-clock (`h < 6`). On the two DST switch
 * mornings a year the rollover lands at 05:00 or 07:00 local instead of
 * 06:00 — festivals are overwhelmingly summer, and the affected hour is
 * 5-7 AM. (Unlike wallClockToUtc, an invalid zone here falls back to the
 * viewer's clock instead of throwing: this runs inside render paths with
 * a DB-sourced value.)
 */
export function festivalDayIso(now: Date = new Date(), timeZone?: string): string {
  const shifted = new Date(now.getTime() - ROLLOVER_MS);
  if (!timeZone) return todayIso(shifted);
  // en-CA renders as yyyy-MM-dd. An invalid zone would throw deep inside a
  // render; fall back to the viewer's own clock instead.
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(shifted);
  } catch {
    return todayIso(shifted);
  }
}

/**
 * The day a production page should open on: today when the festival is
 * running, otherwise the first day. Empty string when there are no days —
 * matching the "nothing selected yet" state the pages use.
 */
export function defaultEventDate(days: string[], today: string): string {
  if (days.length === 0) return "";
  return days.includes(today) ? today : days[0];
}

/**
 * Is `today` inside the production's festival window?
 *
 * Used to keep festival-only prompts quiet the rest of the year: outside the
 * window nothing is tracked anyway, so asking a crew member to switch location
 * sharing on would be noise — and, per the tracking rules, pointless.
 */
export function isFestivalRunning(
  start: string | null | undefined,
  end: string | null | undefined,
  today: string,
): boolean {
  return eventDatesBetween(start, end).includes(today);
}
